import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  updateProfile,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFirebaseAuth, getFirebaseDb, getFirebaseApp } from './firebase';
import type { User } from '../types';

/**
 * Ensures a Firestore user profile exists for the given Firebase Auth user.
 * Creates one if missing (e.g., after first Google sign-in via redirect).
 */
export async function ensureUserProfile(user: FirebaseUser): Promise<void> {
  const db = getFirebaseDb();
  const userDoc = await getDoc(doc(db, 'users', user.uid));

  if (!userDoc.exists()) {
    await setDoc(doc(db, 'users', user.uid), {
      id: user.uid,
      email: user.email,
      displayName: user.displayName || null,
      photoURL: user.photoURL || null,
      defaultCurrency: 'USD',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } else if (user.displayName && !userDoc.data()?.displayName) {
    await updateDoc(doc(db, 'users', user.uid), { displayName: user.displayName });
  }
}

export async function signUpWithEmail(
  email: string,
  password: string,
  displayName?: string
): Promise<FirebaseUser> {
  const auth = getFirebaseAuth();

  const { user } = await createUserWithEmailAndPassword(auth, email, password);

  if (displayName) {
    await updateProfile(user, { displayName });
  }

  // Create user document in Firestore
  await ensureUserProfile(user);

  return user;
}

export async function signInWithEmail(
  email: string,
  password: string
): Promise<FirebaseUser> {
  const auth = getFirebaseAuth();
  const { user } = await signInWithEmailAndPassword(auth, email, password);
  return user;
}

export async function signOut(): Promise<void> {
  const auth = getFirebaseAuth();
  await firebaseSignOut(auth);
}

export async function sendPasswordReset(email: string): Promise<void> {
  const auth = getFirebaseAuth();
  await sendPasswordResetEmail(auth, email);
}

/**
 * Verify a password reset code and return the associated email
 */
export async function verifyPasswordResetCode(oobCode: string): Promise<string> {
  const auth = getFirebaseAuth();
  return await firebaseVerifyPasswordResetCode(auth, oobCode);
}

/**
 * Complete password reset with the code and new password
 */
export async function confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
  const auth = getFirebaseAuth();
  await firebaseConfirmPasswordReset(auth, oobCode, newPassword);
}

export async function getUserProfile(userId: string): Promise<User | null> {
  const db = getFirebaseDb();
  const userDoc = await getDoc(doc(db, 'users', userId));

  if (!userDoc.exists()) {
    return null;
  }

  return userDoc.data() as User;
}

export async function updateUserProfile(
  userId: string,
  data: Partial<Pick<User, 'displayName' | 'defaultCurrency'>>
): Promise<void> {
  const db = getFirebaseDb();
  await setDoc(
    doc(db, 'users', userId),
    {
      ...data,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export function subscribeToAuthState(
  callback: (user: FirebaseUser | null) => void
): () => void {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
}

/**
 * Log a failed login attempt (wrong password) to the server
 * Fire-and-forget — errors are silently caught
 */
export async function logFailedLogin(email: string): Promise<void> {
  try {
    const app = getFirebaseApp();
    const projectId = app.options.projectId;
    const url = `https://us-central1-${projectId}.cloudfunctions.net/logFailedLoginFunction`;

    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch {
    // Fire-and-forget — don't block the error flow
  }
}
